import useDocumentDownload from '@/hooks/api/useDocumentDownload';
import useGetDocument from '@/mobile/hooks/api/useGetDocument';
import { documentState } from '@/mobile/states/documentState';
import { esimState } from '@/mobile/states/esimState';
import { pricePlanExchangeState } from '@/mobile/states/pricePlanExchangeState';
import url from '@/utils/config';
import Link from 'next/link';
import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';

/** 65262_マンション価格改定対応 2024/10/1～2025/7/31(9ヶ月目)以降削除可能です */
import { quitBeforePriceChangedState } from '@/states/selectors/quitBeforePriceChangedState';
/** 65262_マンション価格改定 [ここまで] */

/**
 * コンポーネントのパラメータ情報。
 */
type Props = {
  anchor: string;
  isMobile?: boolean;
};

/**
 * 「よく使われる機能」のコンポーネントです。
 * @param {anchor} アンカー情報
 * @returns コンポーネント情報
 */
const ServiceOftenUseInfo = ({ anchor, isMobile = false }: Props) => {
  const documents = useRecoilValue(documentState);

  // 「ドキュメントダウンロード」のフック処理コンポーネント
  const { executeDownloadContract } = useDocumentDownload();
  const { execute: getMobileDocument } = useGetDocument();

  useEffect(() => {
    if (isMobile) {
      getMobileDocument();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let mobilePath = '';
  if (isMobile) {
    mobilePath = '/mobile';
  }

  /**
   * 「契約内容の確認」を押下したときの処理を実施する。
   */
  const handleClickDownloadContract = () => {
    executeDownloadContract('full');
  };

  const openContractDetailInNewTab = () => {
    // 別タブで開く
    const newTab = window.open(url('/mobile/contract/detail/'), '_blank');
    if (newTab) {
      newTab.focus();
    }
  };

  /** 65262_マンション価格改定対応 2024/10/1～2025/7/31(9ヶ月目)以降削除可能です */
  const isQuitBeforePriceChangedState = useRecoilValue(
    quitBeforePriceChangedState
  ).isQuitBeforePriceState;
  /** 65262_マンション価格改定 [ここまで] */

  // eSIMプロファイルダウンロードの表示制御
  const esimInfo = useRecoilValue(esimState);
  const shouldDisplayEsimIssue =
    !esimInfo.can_display_profile_download && esimInfo.can_display_issue;
  // ご契約プラン変更の表示制御
  const pricePlanExchange = useRecoilValue(pricePlanExchangeState);

  return (
    <div className='m-box' id={anchor}>
      <div className='m-box__inner'>
        <div className='m-heading-l4'>
          <h2 className='m-heading-l4__hdg'>よく使われる機能</h2>
        </div>
        <div className='m-button m-button--third col2'>
          {/* キャッシュバック受け取り */}
          <Link href={`${mobilePath}/campaign/detail/`} className='m-button__inner'>
            <img
              className='m-button__logo'
              src={url('/images/icon_present.svg')}
              alt=''
              width='30'
            />
            <span className='m-button__txt'>キャッシュバック受け取り</span>
            <img className='m-button__icon' src={url('/images/icon-arrow_left.svg')} alt='' />
          </Link>

          {/* 契約内容の確認 */}
          {mobilePath ? (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {documents.can_display && (
                <button className='m-button__inner' onClick={openContractDetailInNewTab}>
                  <img
                    className='m-button__logo'
                    src={url('/images/icon_file.svg')}
                    alt=''
                    width='30'
                  />
                  <span className='m-button__txt'>契約内容の確認</span>
                  <img className='m-button__icon' src={url('/images/icon-arrow_left.svg')} alt='' />
                </button>
              )}
            </>
          ) : (
            <button className='m-button__inner' type='button' onClick={handleClickDownloadContract}>
              <img
                className='m-button__logo'
                src={url('/images/icon_file.svg')}
                alt=''
                width='30'
              />
              <span className='m-button__txt'>契約内容の確認</span>
              <img className='m-button__icon' src={url('/images/icon-arrow_left.svg')} alt='' />
            </button>
          )}

          {/* お支払い方法の変更 */}
          <Link href={`${mobilePath}/contract/payment/`} className='m-button__inner'>
            <img
              className='m-button__logo'
              src={url('/images/icon_payment.svg')}
              alt=''
              width='30'
            />
            <span className='m-button__txt'>お支払い方法の変更</span>
            <img className='m-button__icon' src={url('/images/icon-arrow_left.svg')} alt='' />
          </Link>

          {/* メールアドレスの登録 */}
          <Link href={`${mobilePath}/contract/mail/`} className='m-button__inner'>
            <img className='m-button__logo' src={url('/images/icon_mail.svg')} alt='' width='30' />
            <span className='m-button__txt'>メールアドレスの登録</span>
            <img className='m-button__icon' src={url('/images/icon-arrow_left.svg')} alt='' />
          </Link>

          {/* ご契約プラン変更 */}
          {pricePlanExchange.can_display && (
            <Link href={`${mobilePath}/contract/plan_entry/`} className='m-button__inner'>
              <img
                className='m-button__logo'
                src={url('/images/icon_planchange.svg')}
                alt=''
                width='30'
              />
              <span className='m-button__txt'>ご契約プラン変更</span>
              <img className='m-button__icon' src={url('/images/icon-arrow_left.svg')} alt='' />
            </Link>
          )}

          {/* eSIMプロファイルダウンロード */}
          {esimInfo.can_display_profile_download && (
            <Link href={`${mobilePath}/contract/esim_profile/`} className='m-button__inner'>
              <img
                className='m-button__logo'
                src={url('/images/icon_esim.svg')}
                alt=''
                width='30'
              />
              <span className='m-button__txt'>eSIMプロファイルダウンロード</span>
              <img className='m-button__icon' src={url('/images/icon-arrow_left.svg')} alt='' />
            </Link>
          )}

          {/* eSIM発行 */}
          {shouldDisplayEsimIssue && (
            <Link href={`${mobilePath}/contract/esim/`} className='m-button__inner'>
              <img
                className='m-button__logo'
                src={url('/images/icon_esim.svg')}
                alt=''
                width='30'
              />
              <span className='m-button__txt'>eSIM発行</span>
              <img className='m-button__icon' src={url('/images/icon-arrow_left.svg')} alt='' />
            </Link>
          )}
        </div>
        {/** 65262_マンション価格改定対応 2024/10/1～2025/7/31(9ヶ月目)以降削除可能です */}
        {isQuitBeforePriceChangedState && (
          <div className='m-heading-l4'>
            <ul className='m-list-notice'>
              <li className='m-list-notice__item infomation' style={{ fontWeight: 'bold' }}>
                2024/10/1の料金改定に伴い、「ご契約内容の確認」内に記載の月額基本料金が
                改定後の金額となっている場合がございます。
                <br />
                改定前の料金が記載された契約書面をご希望のお客さまは、サポート窓口まで
                お問い合わせください。
              </li>
            </ul>
          </div>
        )}
        {/** 65262_マンション価格改定 [ここまで] */}
      </div>
    </div>
  );
};

ServiceOftenUseInfo.defaultProps = {
  isMobile: false,
};

export default ServiceOftenUseInfo;
